/**
 * src/admin-ui/src/pages/Dashboard.tsx
 *
 * User and administrator resource dashboard.
 */

import React, { useEffect, useState } from "react";
import {
  AccountBalance as BillingIcon,
  People as PeopleIcon,
  PriceChange as PriceChangeIcon,
  QueryStats as QueryStatsIcon,
  Security as SecurityIcon,
  VpnKey as ApiKeyIcon,
} from "@mui/icons-material";
import { useAuthStore } from "../store/authStore";
import { getApiKeyCount } from "../services/apiKeys";
import { getBalance } from "../services/billing";
import { formatBalls } from "../utils/format";
import { isAdmin } from "../utils/roles";
import ResourceCard from "../components/ResourceCard";
import { FullBleedViewport } from "../components/edge-table/EdgeTableLayout.styles";
import { DashboardGrid, MetricBadge } from "./Dashboard.styles";

interface AdminResource {
  title: string;
  description: string;
  icon: React.ReactNode;
  path?: string;
  url?: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [apiKeyCount, setApiKeyCount] = useState<number | null>(null);
  const [balance, setBalance] = useState<number | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const [keyResult, balanceResult] = await Promise.allSettled([
        getApiKeyCount(),
        getBalance(),
      ]);
      setApiKeyCount(keyResult.status === "fulfilled" ? keyResult.value : 0);
      setBalance(
        balanceResult.status === "fulfilled" ? balanceResult.value.balance : 0,
      );
    };
    fetchDashboardData();
  }, []);

  const menuItems = [
    {
      title: "API Ключи",
      description: "Управление ключами доступа",
      icon: <ApiKeyIcon />,
      path: "/api-keys",
      badge:
        apiKeyCount === null ? undefined : (
          <MetricBadge status="neutral">{apiKeyCount}</MetricBadge>
        ),
    },
    {
      title: "Оплата",
      description: "Баланс и пополнение счёта",
      icon: <BillingIcon />,
      path: "/billing",
      badge:
        balance === null ? undefined : (
          <MetricBadge status={balance === 0 ? "error" : "success"}>
            {formatBalls(balance)}
          </MetricBadge>
        ),
    },
  ];

  const keycloakUrl =
    process.env.REACT_APP_KEYCLOAK_URL || "http://localhost:8080";
  const adminResources: AdminResource[] = [
    {
      title: "Пользователи",
      description: "Баланс и транзакции пользователей",
      icon: <PeopleIcon />,
      path: "/admin/users",
    },
    {
      title: "Цены моделей",
      description: "Управление тарифами моделей",
      icon: <PriceChangeIcon />,
      path: "/admin/pricing",
    },
    {
      title: "Аналитика моделей",
      description: "Использование моделей: запросы и баллы за период",
      icon: <QueryStatsIcon />,
      path: "/admin/model-usage",
    },
    {
      title: "Keycloak",
      description: "Консоль управления пользователями",
      icon: <SecurityIcon />,
      url: `${keycloakUrl.replace(/\/$/, "")}/admin/`,
    },
  ];

  // Full-screen page pattern: no page header; the admin block is
  // distinguished by the card accent instead of a section title. The
  // dashboard has no controls, so no bottom bar (withBar={false}).
  return (
    <FullBleedViewport withBar={false}>
      <DashboardGrid>
        {menuItems.map((item) => (
          <ResourceCard key={item.path} {...item} />
        ))}
      </DashboardGrid>

      {isAdmin(user) && (
        <DashboardGrid>
          {adminResources.map((resource) => (
            <ResourceCard
              key={resource.path || resource.url}
              {...resource}
              accent="secondary"
            />
          ))}
        </DashboardGrid>
      )}
    </FullBleedViewport>
  );
};

export default Dashboard;
